import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import Spinner from '@erikdakoda/ui/components/Spinner';
import TextEditable from '@erikdakoda/ui/components/TextEditable';
import MarkdownText from '@erikdakoda/markdown-ui/components/MarkdownText';
import type { OpenArtifactHandler } from '@erikdakoda/markdown-ui/types';
import { useFindUniqueTodoItem, useUpdateTodoItem } from '@erikdakoda/database/hooks/todo-item';
import type { TodoState } from '@erikdakoda/database/enums';
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { TodoItemForDetailSelect } from '@erikdakoda/todo/types';
import { getTodoItemStateUpdateData } from '@erikdakoda/todo/shared/getTodoItemStateUpdateData';
import { useException } from '@erikdakoda/ui/hooks/useException';
import TodoStateControl from './TodoStateControl';
import ScrollBox from '@erikdakoda/ui/components/ScrollBox';

type TodoPanelProps = {
  todoId: string;
  onClose: () => void;
  onOpenArtifact?: OpenArtifactHandler;
  activeArtifactIdentifier?: string | null;
};

/**
 * Top-left panel for editing a selected todo item with autosave.
 */
export default function TodoPanel({
  todoId,
  onClose,
  onOpenArtifact,
  activeArtifactIdentifier,
}: TodoPanelProps) {
  'use memo';

  const theme = useTheme();
  const exception = useException();
  const { user } = useCurrentUser();
  const updateTodoItem = useUpdateTodoItem().mutateAsync;

  const {
    data: todo,
    isLoading,
    refetch,
  } = useFindUniqueTodoItem({
    where: { id: todoId },
    select: TodoItemForDetailSelect,
  });

  async function saveField(data: { name?: string; state?: TodoState; doneAt?: Date | null }) {
    if (!todo) return;
    try {
      await updateTodoItem({ where: { id: todo.id }, data });
      await refetch();
    } catch (err: unknown) {
      exception(err);
    }
  }

  function handleNameChange(name: string) {
    void saveField({ name });
  }

  function handleStateChange(state: TodoState) {
    if (!user?.email) {
      return;
    }
    void saveField(getTodoItemStateUpdateData(state, { email: user.email, kind: 'human' }));
  }

  if (isLoading) {
    return (
      <Box className="todo-panel" sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <Spinner />
      </Box>
    );
  }

  if (!todo) {
    return null;
  }

  return (
    <Box
      className="todo-panel"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          height: '3rem',
          backgroundColor: 'background.header',
        }}
      >
        <TodoStateControl
          todoId={todo.id}
          state={todo.state}
          onChange={handleStateChange}
          onTrashed={onClose}
        />
        <Typography variant="subtitle1" sx={{ flexGrow: 1, minWidth: 0 }}>
          <TextEditable text={todo.name} setText={handleNameChange} />
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close todo panel">
          <FaIconUvilo icon="xmark" />
        </IconButton>
      </Box>
      <Box sx={{ paddingX: 12, paddingTop: 4, paddingBottom: 4, flexGrow: 1 }}>
        <Box sx={{ height: '100%' }}>
          <ScrollBox scrollTrackOutside>
            <Box
              sx={{
                maxWidth: theme.breakpoints.values.nm,
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <MarkdownText
                content={todo.body ?? ''}
                sx={{ padding: theme.spacing(4) }}
                onOpenArtifact={onOpenArtifact}
                activeArtifactIdentifier={activeArtifactIdentifier}
              />
            </Box>
          </ScrollBox>
        </Box>
      </Box>
    </Box>
  );
}
